/**
 * Admin — modération chat (sanctions, signalements, casier, wordlist).
 * Sous-sections pilotées par la sidebar admin (comme ML / entraînement).
 */

import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../../config';
import type { Session } from '../../types/app';
import AdminSelect from '../../components/AdminSelect';

export type ModerationSection = 'active' | 'reports' | 'casier' | 'wordlist';

export const MODERATION_SECTION_ORDER: ModerationSection[] = [
  'active',
  'reports',
  'casier',
  'wordlist',
];

export const MODERATION_SECTION_LABELS: Record<ModerationSection, string> = {
  active: 'Sanctions actives',
  reports: 'Signalements',
  casier: 'Casier',
  wordlist: 'Lexique',
};

interface ModerationPageProps {
  session: Session;
  section?: ModerationSection;
  onSectionChange?: (section: ModerationSection) => void;
}

interface Sanction {
  id: number;
  pseudo: string;
  type: string;
  scope: string;
  reason: string | null;
  issuedBy: string;
  startsAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
}

interface Report {
  id: number;
  reporter: string;
  target: string;
  roomId: string;
  messageId: string | null;
  textSnapshot: string | null;
  status: string;
  createdAt: string;
  note: string | null;
}

interface WordEntry {
  id: number;
  pattern: string;
  severity: number;
  actionHint: string;
  enabled: boolean;
}

function modFetch(url: string, pseudo: string, init?: RequestInit): Promise<Response> {
  const u = new URL(url, window.location.origin);
  if (!u.searchParams.has('pseudo')) u.searchParams.set('pseudo', pseudo);
  return fetch(u.toString(), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Pseudo': pseudo,
      'X-Moderator-Pseudo': pseudo,
      ...(init?.headers || {}),
    },
  });
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR');
}

const TYPE_OPTIONS = [
  { value: 'warn', label: 'Avertissement' },
  { value: 'mute', label: 'Mute' },
  { value: 'premod', label: 'Pré-modération' },
  { value: 'channel_ban', label: 'Ban canal' },
  { value: 'site_ban', label: 'Ban site Neo' },
];

const SCOPE_OPTIONS = [
  { value: 'global:detente', label: 'Détente' },
  { value: 'global:compet', label: 'Compétition' },
  { value: 'global:*', label: 'Tous chats' },
];

export default function ModerationPage({
  session,
  section: sectionProp,
  onSectionChange,
}: ModerationPageProps) {
  const section = sectionProp ?? 'active';
  const [canSiteBan, setCanSiteBan] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [active, setActive] = useState<Sanction[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [casier, setCasier] = useState<Sanction[]>([]);
  const [casierTotal, setCasierTotal] = useState(0);
  const [casierPseudo, setCasierPseudo] = useState('');
  const [casierType, setCasierType] = useState('all');
  const [casierIssuedBy, setCasierIssuedBy] = useState('');
  const [casierRevoked, setCasierRevoked] = useState<'all' | 'true' | 'false'>('all');
  const [wordlist, setWordlist] = useState<WordEntry[]>([]);
  const [newPattern, setNewPattern] = useState('');

  const [applyTarget, setApplyTarget] = useState('');
  const [applyType, setApplyType] = useState('mute');
  const [applyScope, setApplyScope] = useState('global:detente');
  const [applyMinutes, setApplyMinutes] = useState('60');
  const [applyReason, setApplyReason] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/moderation/check?pseudo=${encodeURIComponent(session.pseudo)}`)
      .then((r) => r.json())
      .then((d) => setCanSiteBan(d.canSiteBan === true))
      .catch(() => {});
  }, [session.pseudo]);

  const loadActive = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await modFetch(`${BACKEND_URL}/api/moderation/sanctions/active`, session.pseudo);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setActive(data.sanctions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [session.pseudo]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await modFetch(`${BACKEND_URL}/api/moderation/reports?status=open`, session.pseudo);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReports(data.reports ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [session.pseudo]);

  const loadCasier = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (casierPseudo.trim()) params.set('pseudo', casierPseudo.trim());
      if (casierType !== 'all') params.set('type', casierType);
      if (casierIssuedBy.trim()) params.set('issuedBy', casierIssuedBy.trim());
      params.set('revoked', casierRevoked);
      params.set('limit', '300');
      const res = await modFetch(
        `${BACKEND_URL}/api/moderation/sanctions?${params.toString()}`,
        session.pseudo,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCasier(data.sanctions ?? []);
      setCasierTotal(data.total ?? (data.sanctions?.length ?? 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [session.pseudo, casierPseudo, casierType, casierIssuedBy, casierRevoked]);

  const loadWordlist = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await modFetch(`${BACKEND_URL}/api/moderation/wordlist`, session.pseudo);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setWordlist(data.entries ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [session.pseudo]);

  useEffect(() => {
    if (section === 'active') void loadActive();
    if (section === 'reports') void loadReports();
    if (section === 'casier') void loadCasier();
    if (section === 'wordlist') void loadWordlist();
  }, [section, loadActive, loadReports, loadCasier, loadWordlist]);

  const revoke = async (id: number) => {
    const res = await modFetch(`${BACKEND_URL}/api/moderation/sanctions/${id}/revoke`, session.pseudo, {
      method: 'POST',
      body: '{}',
    });
    if (!res.ok) {
      setError(`Révocation échouée (${res.status})`);
      return;
    }
    void loadActive();
    void loadCasier();
  };

  const resolveReport = async (id: number, status: 'resolved' | 'dismissed') => {
    const res = await modFetch(`${BACKEND_URL}/api/moderation/reports/${id}/resolve`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) {
      setError(`Résolution échouée (${res.status})`);
      return;
    }
    void loadReports();
  };

  const applySanction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!applyTarget.trim()) return;
    const body: Record<string, unknown> = {
      target: applyTarget.trim(),
      type: applyType,
      scope: applyType === 'site_ban' ? 'site' : applyScope,
      reason: applyReason.trim() || undefined,
    };
    const minutes = parseInt(applyMinutes, 10);
    if (Number.isFinite(minutes) && minutes > 0 && applyType !== 'warn') {
      body.durationMinutes = minutes;
    }
    const res = await modFetch(`${BACKEND_URL}/api/moderation/sanctions`, session.pseudo, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.message || data.error || `HTTP ${res.status}`);
      return;
    }
    setApplyReason('');
    void loadActive();
  };

  const toggleWord = async (entry: WordEntry) => {
    if (!canSiteBan) return;
    await modFetch(`${BACKEND_URL}/api/moderation/wordlist`, session.pseudo, {
      method: 'PUT',
      body: JSON.stringify({ id: entry.id, enabled: !entry.enabled }),
    });
    void loadWordlist();
  };

  const addWord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSiteBan || !newPattern.trim()) return;
    await modFetch(`${BACKEND_URL}/api/moderation/wordlist`, session.pseudo, {
      method: 'PUT',
      body: JSON.stringify({ pattern: newPattern.trim(), severity: 2, enabled: true }),
    });
    setNewPattern('');
    void loadWordlist();
  };

  const applyTypeOptions = canSiteBan
    ? TYPE_OPTIONS
    : TYPE_OPTIONS.filter((o) => o.value !== 'site_ban');

  return (
    <div className="admin-moderation">
      <p className="admin-chat-log-intro">
        Modération quasi-autonome du chat global. L&apos;auto-mod gère flood et lexique
        jusqu&apos;au ban temporaire de canal ; le ban site reste manuel.
        Pour surveiller et agir en temps réel, ouvrez l&apos;onglet Discussions —
        les outils de modération y apparaissent pour les modos / admins.
      </p>

      {onSectionChange && (
        <div className="ml-sections-nav">
          {MODERATION_SECTION_ORDER.map((s) => (
            <button
              key={s}
              type="button"
              className={`ml-section-btn ${section === s ? 'active' : ''}`}
              onClick={() => onSectionChange(s)}
            >
              {MODERATION_SECTION_LABELS[s]}
            </button>
          ))}
        </div>
      )}

      {error && <div className="admin-empty admin-chat-log-error">{error}</div>}
      {loading && <div className="admin-empty">Chargement…</div>}

      {section === 'active' && (
        <>
          <form className="admin-moderation-apply" onSubmit={applySanction}>
            <h3>Appliquer une sanction</h3>
            <div className="admin-moderation-apply-grid">
              <label className="admin-moderation-field">
                <span>Pseudo</span>
                <input value={applyTarget} onChange={(e) => setApplyTarget(e.target.value)} required />
              </label>
              <label className="admin-moderation-field">
                <span>Type</span>
                <AdminSelect
                  aria-label="Type de sanction"
                  value={applyType}
                  options={applyTypeOptions}
                  onChange={setApplyType}
                />
              </label>
              {applyType !== 'site_ban' && (
                <label className="admin-moderation-field">
                  <span>Portée</span>
                  <AdminSelect
                    aria-label="Portée"
                    value={applyScope}
                    options={SCOPE_OPTIONS}
                    onChange={setApplyScope}
                  />
                </label>
              )}
              {applyType !== 'warn' && (
                <label className="admin-moderation-field">
                  <span>Durée (min)</span>
                  <input
                    type="number"
                    min={1}
                    value={applyMinutes}
                    onChange={(e) => setApplyMinutes(e.target.value)}
                  />
                </label>
              )}
              <label className="admin-moderation-field admin-moderation-field--wide">
                <span>Raison</span>
                <input value={applyReason} onChange={(e) => setApplyReason(e.target.value)} />
              </label>
            </div>
            <button type="submit" className="admin-btn primary">Appliquer</button>
          </form>

          <div className="admin-feedback-table-wrapper">
            <table className="admin-feedback-table" data-mobile="cards">
              <thead>
                <tr>
                  <th>Pseudo</th>
                  <th>Type</th>
                  <th>Portée</th>
                  <th>Par</th>
                  <th>Expire</th>
                  <th>Raison</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {active.map((s) => (
                  <tr key={s.id}>
                    <td data-label="Pseudo">{s.pseudo}</td>
                    <td data-label="Type">{s.type}</td>
                    <td data-label="Portée">{s.scope}</td>
                    <td data-label="Par">{s.issuedBy}</td>
                    <td data-label="Expire">{formatWhen(s.expiresAt)}</td>
                    <td data-label="Raison">{s.reason ?? '—'}</td>
                    <td>
                      <button type="button" className="admin-btn" onClick={() => void revoke(s.id)}>
                        Révoquer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {active.length === 0 && !loading && (
              <div className="admin-empty">Aucune sanction active.</div>
            )}
          </div>
        </>
      )}

      {section === 'reports' && (
        <div className="admin-feedback-table-wrapper">
          <table className="admin-feedback-table" data-mobile="cards">
            <thead>
              <tr>
                <th>Date</th>
                <th>Par</th>
                <th>Cible</th>
                <th>Salon</th>
                <th>Message</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td data-label="Date">{formatWhen(r.createdAt)}</td>
                  <td data-label="Par">{r.reporter}</td>
                  <td data-label="Cible">{r.target}</td>
                  <td data-label="Salon">{r.roomId}</td>
                  <td data-label="Message" className="admin-chat-log-text">{r.textSnapshot ?? '—'}</td>
                  <td className="admin-moderation-actions">
                    <button
                      type="button"
                      className="admin-btn"
                      onClick={() => {
                        setCasierPseudo(r.target);
                        onSectionChange?.('casier');
                      }}
                    >
                      Casier
                    </button>
                    <button type="button" className="admin-btn primary" onClick={() => void resolveReport(r.id, 'resolved')}>
                      Traité
                    </button>
                    <button type="button" className="admin-btn" onClick={() => void resolveReport(r.id, 'dismissed')}>
                      Ignorer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {reports.length === 0 && !loading && (
            <div className="admin-empty">Aucun signalement ouvert.</div>
          )}
        </div>
      )}

      {section === 'casier' && (
        <>
          <form
            className="admin-chat-log-filters"
            onSubmit={(e) => {
              e.preventDefault();
              void loadCasier();
            }}
          >
            <div className="admin-moderation-apply-grid">
              <label className="admin-moderation-field">
                <span>Pseudo</span>
                <input
                  value={casierPseudo}
                  onChange={(e) => setCasierPseudo(e.target.value)}
                  placeholder="Filtrer…"
                />
              </label>
              <label className="admin-moderation-field">
                <span>Type</span>
                <AdminSelect
                  aria-label="Filtrer par type"
                  value={casierType}
                  options={[{ value: 'all', label: 'Tous' }, ...TYPE_OPTIONS]}
                  onChange={setCasierType}
                />
              </label>
              <label className="admin-moderation-field">
                <span>Auteur</span>
                <input
                  value={casierIssuedBy}
                  onChange={(e) => setCasierIssuedBy(e.target.value)}
                  placeholder="auto-mod, pseudo…"
                />
              </label>
              <label className="admin-moderation-field">
                <span>Révoqué</span>
                <AdminSelect
                  aria-label="Filtrer révoqué"
                  value={casierRevoked}
                  options={[
                    { value: 'all', label: 'Tous' },
                    { value: 'false', label: 'Non révoqué' },
                    { value: 'true', label: 'Révoqué' },
                  ]}
                  onChange={(v) => setCasierRevoked(v as 'all' | 'true' | 'false')}
                />
              </label>
            </div>
            <div className="admin-chat-log-filters-actions">
              <button type="submit" className="admin-btn primary">Filtrer</button>
              <span className="admin-chat-log-count">
                {casierTotal.toLocaleString('fr-FR')} sanction{casierTotal > 1 ? 's' : ''}
              </span>
            </div>
          </form>
          <div className="admin-feedback-table-wrapper">
            <table className="admin-feedback-table" data-mobile="cards">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Pseudo</th>
                  <th>Type</th>
                  <th>Portée</th>
                  <th>Par</th>
                  <th>Expire</th>
                  <th>Révoqué</th>
                  <th>Raison</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {casier.map((s) => (
                  <tr key={s.id}>
                    <td data-label="Date">{formatWhen(s.startsAt)}</td>
                    <td data-label="Pseudo">{s.pseudo}</td>
                    <td data-label="Type">{s.type}</td>
                    <td data-label="Portée">{s.scope}</td>
                    <td data-label="Par">{s.issuedBy}</td>
                    <td data-label="Expire">{formatWhen(s.expiresAt)}</td>
                    <td data-label="Révoqué">{formatWhen(s.revokedAt)}</td>
                    <td data-label="Raison">{s.reason ?? '—'}</td>
                    <td>
                      {!s.revokedAt && (
                        <button type="button" className="admin-btn" onClick={() => void revoke(s.id)}>
                          Révoquer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {casier.length === 0 && !loading && (
              <div className="admin-empty">Aucune sanction pour ces critères.</div>
            )}
          </div>
        </>
      )}

      {section === 'wordlist' && (
        <>
          {canSiteBan && (
            <form className="admin-chat-log-filters" onSubmit={addWord}>
              <div className="admin-chat-log-filters-row">
                <label>
                  Nouveau motif
                  <input value={newPattern} onChange={(e) => setNewPattern(e.target.value)} />
                </label>
              </div>
              <button type="submit" className="admin-btn primary">Ajouter</button>
            </form>
          )}
          <div className="admin-feedback-table-wrapper">
            <table className="admin-feedback-table" data-mobile="cards">
              <thead>
                <tr>
                  <th>Motif</th>
                  <th>Sévérité</th>
                  <th>Actif</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {wordlist.map((w) => (
                  <tr key={w.id}>
                    <td data-label="Motif">{w.pattern}</td>
                    <td data-label="Sévérité">{w.severity}</td>
                    <td data-label="Actif">{w.enabled ? 'oui' : 'non'}</td>
                    <td>
                      {canSiteBan && (
                        <button type="button" className="admin-btn" onClick={() => void toggleWord(w)}>
                          {w.enabled ? 'Désactiver' : 'Activer'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
